import {
  gddRangeToMsg,
  calculateGdd,
  baskervilleEmin,
} from "../../utils/helpers"
import { format } from "date-fns"

export default function SanJoseScaleLogic(
  modelData,
  data,
  dateOfInterest,
  isModelInSeason,
  userBiofix1 = null,
  userBiofix2 = null,
  userPhenologicalStage = null,
  userPestStage = null
) {
  // console.count("ModelLogic------------------------")
  const { baseFormula, elements, baseFormulaDisplay, base } = modelData
  const { userInputs } = elements
  const {
    beforeBiofix1,
    afterBiofix1,
    afterBiofix2,
    phenologicalStagesBaseFormula,
  } = elements.managementGuide

  const dailyDataWithGdd = calculateGdd(
    data.dailyData,
    base,
    0,
    baskervilleEmin
  ).map((d) => {
    return { ...d, dateDisplay: format(d.date, "yyyy-MM-dd") }
  })

  // console.log(userBiofix1, userBiofix2)
  // User Biofix dates -------------------------------------------------------
  let userBiofix1Index = 0
  if (userBiofix1 !== null) {
    userBiofix1Index = dailyDataWithGdd.findIndex(
      (day) => day.dateDisplay === userBiofix1
    )
  }
  let userBiofix2Index = 0
  if (userBiofix2 !== null) {
    userBiofix2Index = dailyDataWithGdd.findIndex(
      (day) => day.dateDisplay === userBiofix2
    )
  }

  // Recalculating degree-days when biofix1 ----------------------------------
  let gdd1 = 0
  let gdd2 = 0
  let gdd3Pheno = 0
  const mData = dailyDataWithGdd.map((day, i) => {
    const ddPheno = baskervilleEmin(day.mint, day.maxt, 43)
    gdd3Pheno += ddPheno === "N/A" ? 0 : ddPheno

    let p = {
      date: day.date,
      dateDisplay: day.dateDisplay,
      dayOfYear: day.dayOfYear,
      ms: day.ms,
      isForecast: day.isForecast,
      [`${baseFormula}dd`]: day.dd,
      [`${baseFormula}Gdd`]: day.gdd,
      displayBiofix1Input:
        day.gdd >= userInputs.firstInput.displayElement ? true : false,
      biofix1Gdd: "N/A",
      biofix2Gdd: "N/A",
      [`${phenologicalStagesBaseFormula}dd`]: Math.round(ddPheno),
      [`${phenologicalStagesBaseFormula}Gdd`]: Math.round(gdd3Pheno),
    }

    // Recalculate if user has selected a biofix1 date
    if (userBiofix1 !== null) {
      if (i >= userBiofix1Index) {
        gdd1 += day.dd !== "N/A" ? day.dd : 0
        p.biofix1Gdd = Math.round(gdd1)
      }
    } else {
      if (day.gdd >= userInputs.firstInput.biofix1) {
        gdd1 += day.dd !== "N/A" ? day.dd : 0
        p.biofix1Gdd = Math.round(gdd1)
      }
    }

    // Recalculate if user has selected a biofix2 date
    if (userBiofix2 !== null) {
      if (i >= userBiofix2Index) {
        gdd2 += day.dd !== "N/A" ? day.dd : 0
        p.biofix2Gdd = Math.round(gdd2)
      }
    } else {
      if (
        p.biofix1Gdd !== "N/A" &&
        p.biofix1Gdd >= userInputs.secondInput.biofix2
      ) {
        gdd2 += day.dd !== "N/A" ? day.dd : 0
        p.biofix2Gdd = Math.round(gdd2)
      }
    }

    return p
  })
  // console.log(mData)
  ////////////////////////////////////////////////////////////////////////////////////

  let selectedDate = mData.find(
    (day) => day.dayOfYear === dateOfInterest.dayOfYear
  )
  if (!selectedDate) {
    selectedDate = mData.find((d) => d.isForecast)
  }
  // console.log(selectedDate)

  // Biofix Dates ----------------------------------------------------------
  let biofix1 = null
  const b1 = mData.find((day) => day.biofix1Gdd !== "N/A")
  if (typeof b1 !== "undefined") biofix1 = b1

  if (biofix1) {
    if (selectedDate.dayOfYear < biofix1.dayOfYear) biofix1 = null
  }

  let biofix2 = null
  const b2 = mData.find((day) => day.biofix2Gdd !== "N/A")
  if (typeof b2 !== "undefined") biofix2 = b2

  if (
    biofix1 === null ||
    selectedDate.biofix1Gdd < userInputs.secondInput.biofix2
  ) {
    biofix2 = null
  }

  // console.log(biofix1, biofix2)
  ////////////////////////////////////////////////////////////////////////////////////

  // determine biofix message Below user inputs ------------------------------------
  let userInputMsg = ""
  if (isModelInSeason) {
    if (biofix1 === null && biofix2 === null) {
      userInputMsg = userInputs.firstInput.message.beforeBiofix1
    }
    if (biofix1 && biofix2 === null) {
      userInputMsg = userInputs.firstInput.message.afterBiofix1
    }
    if (biofix1 && biofix2) {
      userInputMsg = userInputs.secondInput.message.afterBiofix2
    }
  }
  ////////////////////////////////////////////////////////////////////////////////////

  // User input DD Accumulation ------------------------------------------------------
  let startDate = data.dailyData[0].date.toLocaleDateString()
  let gddDisplay = selectedDate[`${baseFormula}Gdd`]

  if (biofix1) {
    startDate = "first generation first trap catch"
    gddDisplay = selectedDate.biofix1Gdd
  }

  if (biofix2) {
    startDate = "second generation first trap catch"
    gddDisplay = selectedDate.biofix2Gdd
  }

  const accumulationDDMsg = {
    text: `Accumulated degree days ${baseFormulaDisplay} ${startDate} through ${data.dailyData[
      selectedDate.dayOfYear - 1
    ].date.toLocaleDateString()}:`,
    gdd: gddDisplay,
  }
  // console.log(accumulationDDMsg)
  // /////////////////////////////////////////////////////////////////////////////////////

  // Data to display in Results Table --------------------------------------------------
  let tableData = [...mData]
  if (mData.length >= 8) {
    tableData = mData.slice(
      selectedDate.dayOfYear - 3,
      selectedDate.dayOfYear + 5
    )
  }
  // console.log(tableData)
  // //////////////////////////////////////////////////////////////////////////////////////

  // Data in CSV format to export ------------------------------------------------------
  let csvData = []
  if (isModelInSeason) {
    csvData = mData.map((day) => {
      if (biofix2 && biofix1) {
        return {
          Date: day.dateDisplay,
          [`Daily DD ${baseFormulaDisplay}`]: day[`${baseFormula}dd`],
          "From Jan 1": day[`${baseFormula}Gdd`],
          [`From ${format(biofix1.date, "LLL d")}`]: day.biofix1Gdd,
          [`From ${format(biofix2.date, "LLL d")}`]: day.biofix2Gdd,
        }
      }
      if (biofix1 && biofix2 === null) {
        return {
          Date: day.dateDisplay,
          [`Daily DD ${baseFormulaDisplay}`]: day[`${baseFormula}dd`],
          "From Jan 1": day[`${baseFormula}Gdd`],
          [`From ${format(biofix1.date, "LLL d")}`]: day.biofix1Gdd,
        }
      }
      return {
        Date: day.dateDisplay,
        [`Daily DD ${baseFormulaDisplay}`]: day[`${baseFormula}dd`],
        "From Jan 1": day[`${baseFormula}Gdd`],
      }
    })
  }
  // console.log(csvData)
  //////////////////////////////////////////////////////////////////////////////////////

  // Management Guide ------------------------------------------------------------------
  let managementGuide = {
    pestStage: "",
    pestStatus: "",
    pestManagement: "",
    dashboardDisplay: "",
  }

  const msg = gddRangeToMsg(beforeBiofix1, selectedDate[`${baseFormula}Gdd`])
  if (msg && msg !== "N/A") managementGuide = msg

  if (biofix1) {
    const msg = gddRangeToMsg(afterBiofix1, selectedDate.biofix1Gdd)
    if (msg && msg !== "N/A") managementGuide = msg
  }

  if (biofix2) {
    const msg = gddRangeToMsg(afterBiofix2, selectedDate.biofix2Gdd)
    if (msg && msg !== "N/A") managementGuide = msg
  }

  // console.log(managementGuide)
  //////////////////////////////////////////////////////////////////////////////////////

  // Determine Phenological Stage ----------------------------------------------------
  let phenologicalStage = null

  if (isModelInSeason) {
    if (!biofix1) {
      let res = null
      let obj = elements.managementGuide.phenologicalStages
      let gdd = selectedDate[`${phenologicalStagesBaseFormula}Gdd`]
      if (gdd === "N/A") return "N/A"

      for (const [key, message] of Object.entries(obj)) {
        const gddRange = key.split("-")
        const lowerGdd = +gddRange[0]
        const upperGdd = +gddRange[1]

        if (gdd >= lowerGdd && gdd <= upperGdd) {
          if (message.value === "Tight Cluster") {
            if (gdd <= 217) res = { ...message, key }
            if (gdd > 217) {
              res = {
                value: "Tight Cluster",
                beforeBiofix1: "218-9999",
                key,
              }
            }
          } else {
            res = { ...message, key }
          }
        }
      }

      if (userPhenologicalStage) {
        phenologicalStage = userPhenologicalStage
        managementGuide =
          elements.managementGuide.beforeBiofix1[
            userPhenologicalStage.beforeBiofix1
          ]
      } else {
        phenologicalStage = { ...res }
      }
    }
  }
  // console.log(phenologicalStage, selectedDate.date, selectedDate["base43BEGdd"])
  //////////////////////////////////////////////////////////////////////////////////////

  // Determine Pest Stage -----------------------------------------------------------
  let pestStage = null

  if (biofix1 && !biofix2) {
    const res = gddRangeToMsg(
      elements.managementGuide.afterBiofix1,
      selectedDate.biofix1Gdd
    )
    if (res) {
      if (userPestStage) {
        pestStage = { ...userPestStage }
        managementGuide =
          elements.managementGuide.afterBiofix1[userPestStage.key]
      } else {
        pestStage = { ...res, value: res.pestStage }
        managementGuide = { ...res }
      }
    }
  }
  if (biofix1 && biofix2) {
    const res = gddRangeToMsg(
      elements.managementGuide.afterBiofix2,
      selectedDate.biofix2Gdd
    )
    if (res) {
      if (userPestStage) {
        pestStage = { ...userPestStage }
        managementGuide =
          elements.managementGuide.afterBiofix2[userPestStage.key]
      } else {
        pestStage = { ...res, value: res.pestStage }
        managementGuide = { ...res }
      }
    }
  }

  // console.log(pestStage)
  //////////////////////////////////////////////////////////////////////////////////////

  if (!isModelInSeason) {
    managementGuide = { ...elements.managementGuide.outOfSeasonMsg }
  }

  // Dashboard Summary -----------------------------------------------------------------
  let dashboardSummary = {
    message: "",
    fromJan1: {
      text: `Accum. DD ${baseFormulaDisplay} since Jan 1:`,
      gdd: "N/A",
    },
    [biofix1 ? "biofix1" : null]: {
      text: `Accum. DD ${baseFormulaDisplay} from first trap catch 1st generation:`,
      gdd: "N/A",
    },
    [biofix2 ? "biofix2" : null]: {
      text: `Accum. DD ${baseFormulaDisplay} from first trap catch 2nd generation:`,
      gdd: "N/A",
    },
  }

  if (isModelInSeason === false) {
    dashboardSummary.message =
      elements.managementGuide.outOfSeasonMsg.dashboardDisplay
  } else {
    let currentDate = mData.find((d) => d.isForecast)
    if (currentDate) {
      dashboardSummary.message = managementGuide.dashboardDisplay
      dashboardSummary.fromJan1.gdd = currentDate[`${baseFormula}Gdd`]
      if (biofix1) {
        dashboardSummary.biofix1.gdd = currentDate.biofix1Gdd
      }
      if (biofix2) {
        dashboardSummary.biofix2.gdd = currentDate.biofix2Gdd
      }
    }
  }
  // console.log(dashboardSummary)
  //////////////////////////////////////////////////////////////////////////////////////

  return {
    data: mData,
    biofix1,
    biofix2,
    selectedDate,
    userInputMsg,
    accumulationDDMsg,
    tableData,
    csvData,
    managementGuide,
    phenologicalStage,
    pestStage,
    dashboardSummary,
  }
}
